import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { CrClassification, CrPriority } from "@shared/types/supabase.types";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useCreateChangeRequestFlow } from "../api/change-requests-hooks";
import {
  CR_CLASSIFICATIONS,
  CR_PRIORITIES,
  getCrClassificationDescription,
  getCrClassificationLabel,
  getCrPriorityLabel,
} from "../change-requests.constants";
import { CrPriorityIcon } from "./cr-priority-icon";

interface NewChangeRequestDialogProps {
  projectId: string;
  workspaceSlug: string;
  projectSlug: string;
  /** Controlled mode — when provided, the trigger button is not rendered. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function NewChangeRequestDialog({
  projectId,
  workspaceSlug,
  projectSlug,
  open: controlledOpen,
  onOpenChange: onControlledOpenChange,
}: NewChangeRequestDialogProps) {
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const dialogOpen = isControlled ? controlledOpen : internalOpen;
  const setDialogOpen = isControlled ? (onControlledOpenChange ?? (() => {})) : setInternalOpen;

  const [name, setName] = useState("");
  const [classification, setClassification] = useState<CrClassification | null>(null);
  const [priority, setPriority] = useState<CrPriority>("none");

  const navigate = useNavigate();
  const createFlow = useCreateChangeRequestFlow(projectId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !classification) return;

    const cr = await createFlow.mutateAsync({
      name: name.trim(),
      classification,
      priority,
    });

    setDialogOpen(false);
    setName("");
    setClassification(null);
    setPriority("none");

    // CR-27: open the CR itself — no chat is auto-created. The user can start a
    // chat with Kairo and write a description from the CR detail page.
    await navigate({
      to: "/workspaces/$slug/projects/$projectSlug/cr/$crId",
      params: {
        slug: workspaceSlug,
        projectSlug,
        crId: cr.id,
      },
    });
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setName("");
      setClassification(null);
      setPriority("none");
    }
    setDialogOpen(next);
  };

  return (
    <>
      {!isControlled && <Button onClick={() => setDialogOpen(true)}>New change request</Button>}

      <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New change request</DialogTitle>
          </DialogHeader>

          <form
            onSubmit={(e) => {
              void handleSubmit(e);
            }}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cr-name">Name</Label>
              <Input
                id="cr-name"
                placeholder="What are you changing?"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cr-classification">Classification</Label>
              <Select
                value={classification ?? ""}
                onValueChange={(v) => setClassification(v as CrClassification)}
              >
                <SelectTrigger id="cr-classification">
                  <SelectValue placeholder="Select a type">
                    {classification ? getCrClassificationLabel(classification) : undefined}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {CR_CLASSIFICATIONS.map((value) => (
                    <SelectItem key={value} value={value}>
                      <span className="font-medium">{getCrClassificationLabel(value)}</span>
                      <span className="text-muted-foreground ml-2 text-xs">
                        {getCrClassificationDescription(value)}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cr-priority">Priority</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as CrPriority)}>
                <SelectTrigger id="cr-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CR_PRIORITIES.map((value) => (
                    <SelectItem key={value} value={value}>
                      <CrPriorityIcon priority={value} className="size-4" />
                      {getCrPriorityLabel(value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="flex-col items-end gap-2 sm:flex-col">
              <Button
                type="submit"
                disabled={!name.trim() || !classification || createFlow.isPending}
              >
                {createFlow.isPending ? "Creating…" : "Create"}
              </Button>
              <DialogDescription>
                When you click "Create", the change request opens. From there you can write a
                description and start a chat with Kairo whenever you're ready.
              </DialogDescription>
            </DialogFooter>
          </form>

          {!!createFlow.isError && (
            <p className="text-destructive text-sm">{createFlow.error.message}</p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
